import { Vector3, Matrix4, Euler, Plane } from "three";
import { CubemapQuadtree, hasOwnProperty, isDefined } from "../../infra3d";
import { IMAGEDISPLAYMODE } from "../internal";
import { MeasureHandler } from "../measure/internal";
import { GeoFrameCollection } from "./geoframecollection";
import { AuthMode } from "../viewer/AuthMode";
export class CubemapGeoFrameCollection extends GeoFrameCollection {
    constructor(scene, currentgeoframe, geoframearray) {
        super(scene, currentgeoframe, geoframearray);
        this.imagequadtree = null;
        //todo: define typing of sensors
        this.sensors = null;
        this.currentsensor = 0;
        this._mosaic = {
            0: {
                latmin: -50,
                latmax: 45,
                longmin: -36,
                longmax: 36,
            },
            1: {
                latmin: -50,
                latmax: 45,
                longmin: 36,
                longmax: 108,
            },
            2: {
                latmin: -50,
                latmax: 45,
                longmin: 108,
                longmax: 180,
            },
            3: {
                latmin: -50,
                latmax: 45,
                longmin: -180,
                longmax: -108,
            },
            4: {
                latmin: -50,
                latmax: 45,
                longmin: -108,
                longmax: -36,
            },
            5: {
                latmin: 45,
                latmax: 90,
                longmin: -180,
                longmax: 180,
            },
        };
        this._cubefaces = CubemapGeoFrameCollection.cubefaces;
        this.sensors = currentgeoframe.getMeasureInfo().sensors;
        this.sensors.sort((a, b) => {
            return a["pos"] - b["pos"];
        });
        //todo: remove this if we have no old data from cyclomedia or grunder with no standard cubeface ordering.
        if (CubemapGeoFrameCollection.is_sensors_eq_cubefaces(this.sensors))
            this._cubefaces = this.sensors;
        const mosaic = {};
        for (const key in this.sensors) {
            const sensor = this.sensors[key];
            if (hasOwnProperty(sensor, "mosaic")) {
                mosaic[parseInt(sensor["pos"])] = sensor["mosaic"];
            }
            else if (this._mosaic[parseInt(sensor["pos"])]) {
                mosaic[parseInt(sensor["pos"])] = this._mosaic[parseInt(sensor["pos"])];
            }
        }
        this._mosaic = mosaic;
    }
    // TODO: Naming.
    load(frame = null) {
        if (this.onbuffering)
            this.onbuffering(true);
        let framenumber = frame ? this.frameIndex(frame) : 0;
        // Calculate Centroid for this stream:
        if (this.currentgeoframe != null)
            framenumber = this.currentgeoframe.getFramenumber();
        const numEntries = this.geoframearray.length;
        const mlt = 1.0 / numEntries;
        let easting_center = 0;
        let northing_center = 0;
        let height_center = 0;
        for (let i = 0; i < numEntries; i++) {
            easting_center = easting_center + mlt * this.geoframearray[i]["easting"];
            northing_center = northing_center + mlt * this.geoframearray[i]["northing"];
            height_center = height_center + mlt * this.geoframearray[i]["height"];
        }
        //Create the QuadtreeNode
        const urls = [];
        for (const geoframe of this.geoframearray) {
            urls.push(geoframe.dataPath);
        }
        this.infrascene.engine.setCentroid([easting_center, northing_center, height_center]);
        const dm = this.measureinfo.depthmapmeta;
        if (dm.structure == "none" || this.scene.authmode == AuthMode.view) {
            dm.maxzoom = 0;
        }
        const im = this.measureinfo.imagemeta;
        //Set the additional constant for the gamma correction according to sensortype 4 which is a Punktwolkenpanorama
        const gammaAdditionConstant = this.measureinfo.sensorgroup == 4 ? 0.8 : 0;
        this.imagequadtree = new CubemapQuadtree(urls, im.tilesize, im.height, im.height, 1.0, im.minzoom, im.maxzoom, dm.maxzoom, this._mosaic, this._cubefaces, gammaAdditionConstant);
        this.scene.infrascene.multiPerspectiveMode = true;
        this.cachnotreadycbf = () => {
            this.cacheready = false;
            if (this.onbuffering)
                this.onbuffering(true);
        };
        this.imagequadtree.cacheNotReadyEvent = () => {
            this.cachnotreadycbf();
        };
        this.cachereadycbf = () => {
            this.cacheready = true;
            if (this.onbuffering)
                this.onbuffering(false);
        };
        this.imagequadtree.cacheReadyEvent = () => {
            this.cachereadycbf();
        };
        this.imagequadtree.visibleTilesNotReady = () => {
            this.alltilesready = false;
            if (this.onTileLoading)
                this.onTileLoading(true);
        };
        this.imagequadtree.visibleTilesReady = () => {
            this.alltilesready = true;
            if (this.onTileLoading)
                this.onTileLoading(false);
        };
        this.imagequadtree.framechangedcbf = (f) => {
            this._imageChanged();
        };
        this.infrascene.add(this.imagequadtree);
        this.imagequadtree.setActive(true);
        this.imagequadtree.onRotate = (lat, lng, fov) => {
            if (this.onRotate)
                this.onRotate(lat, lng, fov);
            this.updateCubePosition(lat, lng);
        };
        this.imagequadtree.frameskippedcbf = (newframenmbr) => {
            if (newframenmbr > this.geoframearray.length - 1)
                newframenmbr = this.geoframearray.length;
            if (newframenmbr < 0)
                newframenmbr = 0;
            this.currentgeoframe = this.geoframearray[newframenmbr];
        };
        this.imagequadtree.frameskippedcbf = (newframenmbr) => {
            if (newframenmbr > this.geoframearray.length - 1)
                newframenmbr = this.geoframearray.length;
            if (newframenmbr < 0)
                newframenmbr = 0;
            this.currentgeoframe = this.geoframearray[newframenmbr];
        };
        this.infrascene.multiperspectiveCamera.setOrientation([
            this.geoframearray[framenumber]["omega"],
            this.geoframearray[framenumber]["phi"],
            this.geoframearray[framenumber]["kappa"],
        ]);
        this.infrascene.multiperspectiveCamera.setPosition([
            this.geoframearray[framenumber]["easting"],
            this.geoframearray[framenumber]["northing"],
            this.geoframearray[framenumber]["height"],
        ]);
        this.hasloaded = true;
        this.displaymode = IMAGEDISPLAYMODE.FRAMESTEPPING;
        this.searchToFramenumber(framenumber);
        this.infrascene.render();
    }
    unload() {
        this.infrascene.remove(this.imagequadtree);
        this.imagequadtree.destroy();
        this.imagequadtree.setActive(false);
        this.imagequadtree = null;
        this.scene.infrascene.multiPerspectiveMode = false;
        if (this.lookatfc != null) {
            this.lookatfc.destroy();
            this.lookatfc = null;
        }
        this.hasloaded = false;
    }
    updateCubePosition(lat, lng, sensorid) {
        if (!isDefined(sensorid))
            sensorid = this.imagequadtree.latlonToSensorId([lat, lng]);
        if (sensorid < 0)
            return;
        if (this.currentsensor == sensorid)
            return;
        const R_c0_w = MeasureHandler.getRotationMatrix((this.currentgeoframe.omega * Math.PI) / 180, (this.currentgeoframe.phi * Math.PI) / 180, (this.currentgeoframe.kappa * Math.PI) / 180);
        const sensorPos = this.sensors[sensorid].position;
        const r_c0_c1 = new Vector3(sensorPos.dx, sensorPos.dy, sensorPos.dz);
        r_c0_c1.applyMatrix4(R_c0_w);
        this.infrascene.multiperspectiveCamera.setPosition([
            this.currentgeoframe.easting + r_c0_c1.x,
            this.currentgeoframe.northing + r_c0_c1.y,
            this.currentgeoframe.height + r_c0_c1.z,
        ]);
        this.currentsensor = sensorid;
    }
    _imageChanged() {
        const R_c0_w = MeasureHandler.getRotationMatrix((this.currentgeoframe.omega * Math.PI) / 180, (this.currentgeoframe.phi * Math.PI) / 180, (this.currentgeoframe.kappa * Math.PI) / 180);
        const sensorPos = this.sensors[this.currentsensor].position;
        const r_c0_c1 = new Vector3(sensorPos.dx, sensorPos.dy, sensorPos.dz);
        r_c0_c1.applyMatrix4(R_c0_w);
        this.infrascene.multiperspectiveCamera.setPosition([
            this.currentgeoframe.easting + r_c0_c1.x,
            this.currentgeoframe.northing + r_c0_c1.y,
            this.currentgeoframe.height + r_c0_c1.z,
        ]);
        this.infrascene.multiperspectiveCamera.setPosition([
            this.currentgeoframe.easting + r_c0_c1.x,
            this.currentgeoframe.northing + r_c0_c1.y,
            this.currentgeoframe.height + r_c0_c1.z,
        ]);
        this.infrascene.multiperspectiveCamera.setOrientation([
            this.currentgeoframe.omega,
            this.currentgeoframe.phi,
            this.currentgeoframe.kappa,
        ]);
        this.infrascene.render();
        //determine inclination at current position for 2d-feature plane
        const idx = this.currentgeoframe ? this.frameIndex(this.currentgeoframe) : 0;
        let cidx = idx - 1;
        let nidx = idx + 1;
        if (idx + 1 == this.geoframearray.length) {
            cidx = idx - 1;
            nidx = idx;
        }
        if (idx == 0)
            cidx = idx;
        const p1 = new Vector3(this.geoframearray[cidx]["easting"], this.geoframearray[cidx]["northing"], this.geoframearray[cidx]["height"]);
        const p2 = new Vector3(this.geoframearray[nidx]["easting"], this.geoframearray[nidx]["northing"], this.geoframearray[nidx]["height"]);
        const v1 = p1.clone();
        v1.sub(p2);
        //define the plane
        const n = new Vector3(v1.y, -v1.x, 0); //orthognal of v1 in 2d space
        n.cross(v1);
        p1.z = p1.z - this.currentgeoframe.getMeasureInfo().height;
        const plane = new Plane().setFromNormalAndCoplanarPoint(n, p1);
        this.infrascene.setGroundPlane(plane);
        if (this.ongeoframechanged !== null)
            this.ongeoframechanged(this.currentgeoframe);
    }
    /**
     * Fits the image into the current canvas-size
     * @method fitToCanvas
     * @param {string} priority - not used in panostream
     */
    fitToCanvas(priority) {
        this.infrascene.multiperspectiveCamera.setLatLon(this.measureinfo.tilt, this.measureinfo.orientation, 70);
    }
    getCubeOrientations() {
        const cubeorientations = [];
        for (const cubeface of this._cubefaces) {
            cubeorientations.push(cubeface.position);
        }
        return cubeorientations;
    }
    latlonToSensorId(latlon) {
        if (latlon.length !== 2)
            return -1;
        let sensorid = -1;
        const lat = latlon[0];
        const lon = latlon[1];
        const mod = (n, m) => {
            return ((n % m) + m) % m;
        };
        for (const key in this._mosaic) {
            const current = this._mosaic[key];
            const shift = mod(current["longmin"], 360);
            const _longmax = mod(current["longmax"] - shift, 360);
            const _lon = mod(lon - shift, 360);
            if (_lon > _longmax)
                continue;
            if (lat < current["latmin"])
                continue;
            if (lat > current["latmax"])
                continue;
            sensorid = parseInt(key);
            break;
        }
        return sensorid;
    }
    mapToSensorId(nx, ny, cubefaceindex) {
        const ppX = nx - 0.5;
        const ppY = -(ny - 0.5);
        const ppZ = -0.5;
        const coords3d = new Vector3(ppX, ppY, ppZ);
        const cubeface = this._cubefaces[cubefaceindex];
        const euler = new Euler((cubeface.position.domega * Math.PI) / 180, (cubeface.position.dphi * Math.PI) / 180, (cubeface.position.dkappa * Math.PI) / 180);
        const rotmat = new Matrix4().makeRotationFromEuler(euler);
        coords3d.applyMatrix4(rotmat);
        const lat = Math.atan(coords3d.y / Math.sqrt(Math.pow(coords3d.x, 2) + Math.pow(coords3d.y, 2))) * (180 / Math.PI);
        const lon = Math.atan2(coords3d.x, coords3d.z * -1) * (180 / Math.PI);
        return this.latlonToSensorId([lat, lon]);
    }
    static is_sensors_eq_cubefaces(sensors) {
        if (sensors.length != 6)
            return false;
        let sensors_eq_cubefaces = true;
        for (const key in sensors) {
            const sensor = sensors[key];
            for (const key3 in sensor["position"]) {
                const pos_value = sensor["position"][key3];
                if (["dx", "dy", "dz"].indexOf(key3) > -1) {
                    sensors_eq_cubefaces = sensors_eq_cubefaces && pos_value == 0;
                }
                else {
                    sensors_eq_cubefaces =
                        sensors_eq_cubefaces && [0, 90, 180, 270, 360, -90, -180].indexOf(pos_value) > -1;
                }
            }
        }
        return sensors_eq_cubefaces;
    }
    getLatLon() {
        return this.infrascene.multiperspectiveCamera.getLatLon();
    }
    align_panoramic_view(current_geoframe, new_geoframe, reset = false) {
        const future_latlon = GeoFrameCollection.calcfuturelatlon(current_geoframe, new_geoframe, this.scene.panoramaresetthreshold, reset);
        this.infrascene.multiperspectiveCamera.setInitLatLon(future_latlon[0], future_latlon[1]);
    }
    static get cubefaces() {
        return [
            {
                position: {
                    domega: 0.0,
                    dphi: 0.0,
                    dkappa: 0.0,
                },
                pos: "0",
            },
            {
                position: {
                    domega: 0.0,
                    dphi: 180.0,
                    dkappa: 0.0,
                },
                pos: "1",
            },
            {
                position: {
                    domega: 0.0,
                    dphi: 90.0,
                    dkappa: 0.0,
                },
                pos: "2",
            },
            {
                position: {
                    domega: 0.0,
                    dphi: 270.0,
                    dkappa: 0.0,
                },
                pos: "3",
            },
            {
                position: {
                    domega: 90.0,
                    dphi: 0.0,
                    dkappa: 0.0,
                },
                pos: "4",
            },
            {
                position: {
                    domega: 270.0,
                    dphi: 0.0,
                    dkappa: 0.0,
                },
                pos: "5",
            },
        ];
    }
}
//# sourceMappingURL=cubemapgeoframecollection.js.map